import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FilmSlate, DownloadSimple, WarningCircle, Clock, Check, PencilSimple, CaretDown } from "@phosphor-icons/react";
import { getExportPreview, updateShotScreenDuration, buildExportPackage } from "@/lib/api";
import { toast } from "sonner";

function formatRuntime(totalSeconds) {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function DurationEditor({ shotId, seconds, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(seconds);

  useEffect(() => setValue(seconds), [seconds]);

  const save = async () => {
    const n = parseInt(value, 10);
    if (!n || n < 1 || n > 30) {
      toast.error("Duration must be between 1 and 30 seconds");
      return;
    }
    try {
      await updateShotScreenDuration(shotId, n);
      toast.success(`Screen duration set to ${n}s`);
      setEditing(false);
      onSaved(shotId, n);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update duration");
    }
  };

  if (editing) {
    return (
      <span className="flex items-center gap-1">
        <Input
          data-testid={`export-shot-duration-input-${shotId}`}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          type="number"
          min={1}
          max={30}
          className="h-6 w-14 bg-slate-950 border-slate-800 rounded-sm text-xs px-1.5"
        />
        <button data-testid={`export-shot-duration-save-${shotId}`} onClick={save} className="text-emerald-400 hover:text-emerald-300">
          <Check size={13} />
        </button>
      </span>
    );
  }

  return (
    <button
      data-testid={`export-shot-duration-edit-${shotId}`}
      onClick={() => setEditing(true)}
      className="flex items-center gap-1 text-slate-300 hover:text-blue-400 transition-colors font-mono"
    >
      <Clock size={11} />
      {seconds}s
      <PencilSimple size={10} className="text-slate-600" />
    </button>
  );
}

export default function ExportPanel({ storyId, refreshSignal }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [building, setBuilding] = useState(false);
  const [showExcluded, setShowExcluded] = useState(false);

  const load = async () => {
    try {
      const data = await getExportPreview(storyId);
      setPreview(data);
    } catch (e) {
      toast.error("Failed to load export preview");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storyId, refreshSignal]);

  const handleDurationSaved = (shotId, seconds) => {
    setPreview((prev) => {
      if (!prev) return prev;
      const included = prev.included.map((s) => (s.id === shotId ? { ...s, screen_duration_seconds: seconds } : s));
      const total = included.reduce((sum, s) => sum + s.screen_duration_seconds, 0);
      return { ...prev, included, total_estimated_runtime_seconds: total };
    });
  };

  const handleBuild = async () => {
    setBuilding(true);
    try {
      const blob = await buildExportPackage(storyId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(preview?.story_title || "story").replace(/\s+/g, "_")}_resolve_export.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export package downloaded — see README.txt inside for DaVinci Resolve import steps");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to build export package");
    } finally {
      setBuilding(false);
    }
  };

  if (loading) return null;
  if (!preview) return null;

  const includedCount = preview.included.length;
  const excludedCount = preview.excluded.length;

  return (
    <div data-testid="export-panel" className="border border-slate-800 rounded-sm p-3 bg-slate-900/50 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-heading uppercase tracking-wider text-xs font-semibold text-slate-400 flex items-center gap-2">
          <FilmSlate size={14} /> Phase 4 — DaVinci Resolve Draft Export
        </h3>
        <div data-testid="export-stats" className="flex items-center gap-3 font-mono text-[11px] text-slate-500">
          <span className="text-emerald-400">{includedCount} ready</span>
          {excludedCount > 0 && <span className="text-amber-400">{excludedCount} excluded</span>}
          <span data-testid="export-total-runtime" className="text-slate-300">
            <Clock size={11} className="inline mr-1 -mt-0.5" />
            {formatRuntime(preview.total_estimated_runtime_seconds)} est. runtime
          </span>
        </div>
      </div>

      {includedCount === 0 ? (
        <p className="text-xs text-slate-600 italic py-2">
          No shots have passed The Warden's QC yet — approve at least one shot before exporting.
        </p>
      ) : (
        <div data-testid="export-included-list" className="border border-slate-800 rounded-sm divide-y divide-slate-800 max-h-72 overflow-y-auto">
          {preview.included.map((s) => (
            <div key={s.id} className="flex items-center justify-between px-3 py-1.5 text-xs">
              <span className="flex items-center gap-2 text-slate-400 truncate flex-1">
                <span className="font-mono text-slate-600 shrink-0">
                  S{String(s.scene_number).padStart(2, "0")}_SH{String(s.shot_number).padStart(2, "0")}
                </span>
                <span className="truncate">{s.pose_action}</span>
              </span>
              <span className="shrink-0 pl-3 flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-wide text-slate-600">{s.output_type}</span>
                {s.is_still ? (
                  <DurationEditor shotId={s.id} seconds={s.screen_duration_seconds} onSaved={handleDurationSaved} />
                ) : (
                  <span className="flex items-center gap-1 text-slate-500 font-mono">
                    <Clock size={11} /> {s.screen_duration_seconds}s
                  </span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}

      {excludedCount > 0 && (
        <div>
          <button
            data-testid="export-excluded-toggle"
            onClick={() => setShowExcluded((v) => !v)}
            className="flex items-center gap-1 text-[11px] text-amber-500 hover:text-amber-400 transition-colors"
          >
            <WarningCircle size={12} />
            {excludedCount} shot(s) not yet approved will be SKIPPED in this export
            <CaretDown size={10} className={`transition-transform ${showExcluded ? "rotate-180" : ""}`} />
          </button>
          {showExcluded && (
            <div data-testid="export-excluded-list" className="mt-2 border border-slate-800 rounded-sm divide-y divide-slate-800">
              {preview.excluded.map((s) => (
                <div key={s.id} className="flex items-center justify-between px-3 py-1.5 text-xs text-slate-500">
                  <span className="font-mono">
                    S{String(s.scene_number).padStart(2, "0")}_SH{String(s.shot_number).padStart(2, "0")}
                  </span>
                  <span className="text-[10px] uppercase tracking-wide text-slate-600">{s.gen_status}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end">
        <Button
          data-testid="build-export-btn"
          onClick={handleBuild}
          disabled={building || includedCount === 0}
          className="bg-fuchsia-600 hover:bg-fuchsia-500 text-white rounded-sm"
        >
          <DownloadSimple size={14} className="mr-2" />
          {building ? "Building Package…" : `Build Export Package (${includedCount} shots)`}
        </Button>
      </div>
    </div>
  );
}
